import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { format } from 'date-fns';
import { formatBareDate } from '@/lib/etDate';

interface TrashStat {
  player_id: string;
  full_name: string;
  team: string;
  position: string;
  game_date: string;
  trash_points: number;
  strikeouts: number;
  errors: number;
  earned_runs: number;
  walks_allowed: number;
  blown_saves: number;
  wild_pitches: number;
  is_pitcher: boolean;
}

const MLBBetaLeaderboard = () => {
  const [stats, setStats] = useState<TrashStat[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    const fetchStats = async () => {
      // Only show loading skeleton on initial load, not date changes
      if (stats.length === 0) setLoading(true);
      const { data, error } = await supabase
        .from('player_game_stats')
        .select('*, players!inner(full_name, mlb_team_abbreviation, position_abbreviation)')
        .eq('game_date', selectedDate)
        .gt('trash_points', 0)
        .order('trash_points', { ascending: false })
        .limit(50);

      if (!error && data) {
        setStats(data.map((row: any) => ({
          player_id: row.player_id,
          full_name: row.players.full_name,
          team: row.players.mlb_team_abbreviation || 'FA',
          position: row.players.position_abbreviation,
          game_date: row.game_date,
          trash_points: Number(row.trash_points),
          strikeouts: row.strikeouts,
          errors: row.errors,
          earned_runs: row.earned_runs,
          walks_allowed: row.walks_allowed,
          blown_saves: row.blown_saves,
          wild_pitches: row.wild_pitches,
          is_pitcher: row.is_pitcher,
        })));
      }
      setLoading(false);
    };
    fetchStats();
  }, [selectedDate]);

  const changeDate = (offset: number) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + offset);
    setSelectedDate(d.toISOString().split('T')[0]);
  };

  return (
    <div className="space-y-4 pb-20 md:pb-0">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold font-heading text-foreground">🗑️ Trash Leaderboard</h1>
        <div className="flex items-center gap-2">
          <button onClick={() => changeDate(-1)} className="px-2 py-1 text-sm rounded bg-muted text-foreground hover:bg-muted/80">←</button>
          <span className="text-sm font-medium text-foreground min-w-[100px] text-center">
            {formatBareDate(selectedDate, { month: 'short', day: 'numeric', year: 'numeric' })}
          </span>
          <button onClick={() => changeDate(1)} className="px-2 py-1 text-sm rounded bg-muted text-foreground hover:bg-muted/80">→</button>
        </div>
      </div>

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-14" />)}
        </div>
      ) : stats.length === 0 ? (
        <Card className="bg-card border-border">
          <CardContent className="p-8 text-center text-muted-foreground">
            No trash stats for this date. Games may not have started yet.
          </CardContent>
        </Card>
      ) : (
        <div className="rounded-lg border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50 text-muted-foreground text-xs">
                <th className="text-left px-3 py-2 w-8">#</th>
                <th className="text-left px-3 py-2">Player</th>
                <th className="text-center px-2 py-2 hidden md:table-cell">K</th>
                <th className="text-center px-2 py-2 hidden md:table-cell">ER</th>
                <th className="text-center px-2 py-2 hidden md:table-cell">E</th>
                <th className="text-center px-2 py-2 hidden md:table-cell">BB</th>
                <th className="text-center px-2 py-2 hidden md:table-cell">BS</th>
                <th className="text-center px-2 py-2 hidden md:table-cell">WP</th>
                <th className="text-right px-3 py-2 font-bold">TP</th>
              </tr>
            </thead>
            <tbody>
              {stats.map((s, i) => (
                <tr key={`${s.player_id}-${s.game_date}`} className="border-t border-border/50 hover:bg-muted/30">
                  <td className="px-3 py-2.5 font-bold text-muted-foreground">{i + 1}</td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-foreground">{s.full_name}</span>
                      <Badge variant="outline" className="text-[9px] px-1 py-0 h-4">{s.team}</Badge>
                      <Badge variant="secondary" className="text-[9px] px-1 py-0 h-4">{s.position}</Badge>
                    </div>
                  </td>
                  <td className="text-center px-2 py-2.5 hidden md:table-cell text-muted-foreground">{s.strikeouts || '-'}</td>
                  <td className="text-center px-2 py-2.5 hidden md:table-cell text-muted-foreground">{s.earned_runs || '-'}</td>
                  <td className="text-center px-2 py-2.5 hidden md:table-cell text-muted-foreground">{s.errors || '-'}</td>
                  <td className="text-center px-2 py-2.5 hidden md:table-cell text-muted-foreground">{s.walks_allowed || '-'}</td>
                  <td className="text-center px-2 py-2.5 hidden md:table-cell text-muted-foreground">{s.blown_saves || '-'}</td>
                  <td className="text-center px-2 py-2.5 hidden md:table-cell text-muted-foreground">{s.wild_pitches || '-'}</td>
                  <td className="text-right px-3 py-2.5 font-bold text-primary">{s.trash_points}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default MLBBetaLeaderboard;
